"use strict";

import _defineProperty from "@babel/runtime-corejs3/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.string.starts-with.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { saveMissingKeys } from '@autoguru/i18n-missing-keys-handler';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import { getAllLanguageCodes } from './languages';
import { ManifestAwareBackend } from './manifest-aware-backend';
import { getBasePath } from './utils/get-base-path';
import { getDataDogRUM } from '@autoguru/logs/datadog';
const supportedLanguages = getAllLanguageCodes();
// Default namespaces - MFEs should provide their own namespaces
const namespaces = [];

// Performance monitoring wrapper
const withPerformanceMonitoring = (backend, enableMonitoring = true) => {
  var _backend$read, _backend$prototype, _backend$prototype2;
  if (!enableMonitoring) return backend;
  const originalRead = ((_backend$read = backend.read) === null || _backend$read === void 0 ? void 0 : _backend$read.bind(backend)) || ((_backend$prototype = backend.prototype) === null || _backend$prototype === void 0 || (_backend$prototype = _backend$prototype.read) === null || _backend$prototype === void 0 ? void 0 : _backend$prototype.bind(backend));
  if (!originalRead) return backend;
  const monitoredRead = function monitoredRead(language, namespace, callback) {
    const startTime = performance.now();
    const mark = `i18n_load_${language}_${namespace}_start`;
    if (typeof performance !== 'undefined') {
      performance.mark(mark);
    }
    const wrappedCallback = (err, data) => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      if (typeof performance !== 'undefined') {
        performance.measure(`i18n_load_${language}_${namespace}`, mark);
      }

      // Log slow loads
      if (__DEBUG__ && duration > 500) {
        console.warn(`[i18n] Slow translation load: ${language}/${namespace} took ${duration}ms`);
      }

      // Report to monitoring services
      if (typeof window !== 'undefined') {
        // New Relic
        if (window.newrelic && typeof window.newrelic.addPageAction === 'function') {
          window.newrelic.addPageAction('i18n_load', {
            language,
            namespace,
            duration,
            success: !err,
            timestamp: Date.now()
          });
        }

        // DataDog RUM - use window.datadogRum (set by app-shell)
        const datadogRum = getDataDogRUM();
        if (datadogRum !== null && datadogRum !== void 0 && datadogRum.addAction) {
          datadogRum.addAction('i18n_load', {
            language,
            namespace,
            duration,
            success: !err
          });
        }

        // Generic analytics
        if (window.analytics) {
          window.analytics.track('Translation Loaded', {
            language,
            namespace,
            duration,
            fromCache: duration < 50,
            // Assume cache if very fast
            success: !err
          });
        }
      }
      callback(err, data);
    };
    return originalRead.call(this, language, namespace, wrappedCallback);
  };

  // Apply the monitored read function
  if (backend.read) {
    backend.read = monitoredRead;
  } else if ((_backend$prototype2 = backend.prototype) !== null && _backend$prototype2 !== void 0 && _backend$prototype2.read) {
    backend.prototype.read = monitoredRead;
  }
  return backend;
};

// Create and configure the manifest-aware backend
const manifestBackend = new ManifestAwareBackend();
const initOptions = {
  fallbackLng: 'en',
  supportedLngs: supportedLanguages,
  ns: [],
  // No default namespaces - MFEs provide their own
  defaultNS: false,
  // Disable default namespace completely

  // Allow initialization without resources
  partialBundledLanguages: true,
  returnNull: false,
  returnEmptyString: false,
  // Load behavior - don't fail on missing namespaces
  load: 'currentOnly',
  // Fallback behavior
  fallbackNS: false,
  // Don't fallback to other namespaces

  // Enable missing key tracking
  saveMissing: true,
  saveMissingTo: 'fallback',
  missingKeyNoValueFallbackToKey: true,
  // Performance optimisations
  react: {
    useSuspense: true,
    bindI18n: 'languageChanged loaded',
    bindI18nStore: 'added removed',
    transEmptyNodeValue: '',
    // Prevent React warnings
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'span']
  },
  // Backend configuration for production with manifest support
  // Use only ManifestAwareBackend since it handles caching internally
  backend: {
    publicPath: (() => {
      const path = getBasePath();
      if (__DEBUG__) {
        console.log('[i18n] Detected publicPath:', path || '(root)');
      }
      return path;
    })(),
    cacheStrategy: 'both',
    // Use both memory and localStorage
    cacheExpirationTime: 7 * 24 * 60 * 60 * 1000,
    // 7 days
    enablePerformanceMonitoring: true,
    enableFallback: true,
    // Fallback to non-hashed files if manifest fails
    retryAttempts: 3,
    retryDelay: 1000
  },
  // Detection configuration
  detection: {
    order: ['querystring', 'cookie', 'localStorage', 'sessionStorage', 'navigator'],
    lookupQuerystring: 'lng',
    lookupCookie: 'i18next',
    lookupLocalStorage: 'i18nextLng',
    lookupSessionStorage: 'i18nextLng',
    caches: ['localStorage', 'cookie'],
    cookieOptions: {
      path: '/',
      sameSite: 'strict',
      secure: true // Enable in production
    }
  },
  // Logging and debugging
  debug: __DEBUG__,
  missingKeyHandler: async (lng, ns, key, fallbackValue) => {
    // Save missing keys using the handler
    await saveMissingKeys(lng, ns, key, fallbackValue);
    if (__DEBUG__) {
      console.warn(`[i18n] Missing translation: ${ns}:${key} [${lng}]`);
    }

    // Report to monitoring in production
    if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
      // Sentry integration
      if (window.Sentry) {
        window.Sentry.captureMessage(`Missing translation: ${ns}:${key} [${lng.join(', ')}]`, 'warning');
      }

      // New Relic integration
      if (window.newrelic && typeof window.newrelic.addPageAction === 'function') {
        window.newrelic.addPageAction('i18n_missing_translation', {
          namespace: ns,
          key,
          languages: lng.join(','),
          timestamp: Date.now()
        });
      }
    }
  },
  interpolation: {
    escapeValue: false,
    // React already escapes
    format: (value, format, lng) => {
      // Add custom formatters here
      if (format === 'uppercase' && typeof value === 'string') {
        return value.toUpperCase();
      }
      if (format === 'lowercase' && typeof value === 'string') {
        return value.toLowerCase();
      }
      if (format === 'capitalize' && typeof value === 'string') {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
      if (format === 'currency' && typeof value === 'number') {
        return new Intl.NumberFormat(lng || 'en', {
          style: 'currency',
          currency: 'AUD'
        }).format(value);
      }
      if (format === 'number' && typeof value === 'number') {
        return new Intl.NumberFormat(lng || 'en').format(value);
      }
      if (format === 'percent' && typeof value === 'number') {
        return new Intl.NumberFormat(lng || 'en', {
          style: 'percent',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(value);
      }
      if (format === 'date' && (value instanceof Date || typeof value === 'string')) {
        const date = value instanceof Date ? value : new Date(value);
        return new Intl.DateTimeFormat(lng || 'en').format(date);
      }
      return String(value);
    }
  }
};

// Production-optimised configuration with manifest support
const i18nInitPromise = i18n.use(withPerformanceMonitoring(manifestBackend)) // Use manifest-aware backend directly
.use(LanguageDetector).use(ICU).use(initReactI18next).init(initOptions).catch(error => {
  if (__DEBUG__) {
    console.error('[i18n] Failed to initialize i18n:', error);
  }
  // Ensure i18n is in a usable state even if initialization partially fails
  if (!i18n.isInitialized) {
    // Force basic initialization with empty resources
    i18n.init(_objectSpread(_objectSpread({}, initOptions), {}, {
      resources: {},
      lng: 'en',
      initImmediate: false
    }));
  }
  return i18n;
});

// Export helper functions for manual control
export const preloadNamespaces = async (namespaces, language) => {
  const lng = language || i18n.language || 'en';

  // Safety check for valid namespaces
  if (!namespaces || namespaces.length === 0) {
    return;
  }
  try {
    await i18n.loadNamespaces(namespaces);
  } catch (error) {
    if (__DEBUG__) {
      console.warn('[i18n] Failed to preload namespaces:', namespaces, error);
    }
  }

  // Report preload metrics
  if (typeof window !== 'undefined' && window.newrelic && typeof window.newrelic.addPageAction === 'function') {
    window.newrelic.addPageAction('i18n_preload', {
      namespaces: namespaces.join(','),
      language: lng,
      timestamp: Date.now()
    });
  }
};
export const getCacheStats = () => {
  // This would need to be implemented in ManifestAwareBackend
  // For now, return placeholder values
  return {
    memoryCacheSize: 0,
    localStorageCacheSize: 0,
    cacheHitRate: 0
  };
};
export const clearTranslationCache = () => {
  // Clear localStorage cache
  if (typeof localStorage !== 'undefined') {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('i18n_') || key.startsWith('i18next'))) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  }

  // Clear memory cache would require access to backend instance
  if (__DEBUG__) {
    console.log('[i18n] Translation cache cleared');
  }
};

// Export initialisation promise for apps that need to ensure i18n is ready
export const i18nReady = i18nInitPromise;

// Expose i18n instance globally for date-utils and other packages
if (typeof window !== 'undefined') {
  window.i18next = i18n;
}
export default i18n;
export { supportedLanguages, namespaces };